import { AxisAlignedBoundingBox, Cartesian3, Color, MaterialAppearance, Matrix4, Transforms, WindingOrder, } from "cesium";
import { composeModelMatrix, computeAnchorLocal, computeAnchorLocalOffset, computeLocalAABBFromVertices, computeLocalCenterFromAnchor, getOBBVerticesFromShapeConfig, localFrameFromEcefSamples, localFrameSamplePoints, } from "./solid-mesh-geometry";
// Re-export: historical import path (pure math now lives in solid-mesh-geometry)
export { computeAnchorLocalOffset } from "./solid-mesh-geometry";
/**
 * Resolves the linear local CRS frame at localOrigin through the CRS converter.
 * @param localOrigin - Frame origin in local coordinates; keep it near the mesh
 * @param ecefOrigin - Known ECEF position of the origin, so the center is not round-tripped again
 * @returns Linearized local CRS frame
 */
export async function resolveLocalFrame(crsConverter, localOrigin, ecefOrigin) {
    const samples = await crsConverter.toWgs84(localFrameSamplePoints(localOrigin));
    return localFrameFromEcefSamples(localOrigin, samples, ecefOrigin);
}
/**
 * SolidMesh class that wraps Cesium Primitive and provides transformation operations
 */
export class SolidMesh {
    _renderedObject;
    _metaData;
    _removePrimitive;
    _addPrimitiveToScene;
    _isMirrored = null;
    /**
     * In-memory baseline quaternion. Captures the orientation at creation/load
     * time so that the UI can display rotation relative to this baseline.
     * NOT persisted to DB.
     */
    _baselineQuaternion;
    _crsConverter;
    _localFrame;
    _localCenterStale = false;
    constructor(options) {
        const { id, shapeConfig, renderedObject, addPrimitiveFn, removePrimitiveFn, crsConverter, attributes, } = options;
        this._crsConverter = crsConverter;
        this._localFrame = options.localFrame;
        const defaultAttributes = {
            anchorType: "bottom-nw",
        };
        this._metaData = {
            id,
            shapeConfig,
            attributes: { ...defaultAttributes, ...attributes },
        };
        this._renderedObject = renderedObject;
        this._addPrimitiveToScene = addPrimitiveFn;
        this._removePrimitive = removePrimitiveFn;
        this._baselineQuaternion =
            options.baselineQuaternion?.clone() ?? shapeConfig.quaternion.clone();
        this.recomposeModelMatrix();
        this.updateAppearanceForScale(this._metaData.shapeConfig.scale);
        // Add primitive to scene; set visibility (false if created hidden)
        this._renderedObject.show = options.visible !== false;
        this._addPrimitiveToScene();
    }
    get id() {
        return this._metaData.id;
    }
    get shapeConfig() {
        return this._metaData.shapeConfig;
    }
    get attributes() {
        return this._metaData.attributes;
    }
    get baselineQuaternion() {
        return this._baselineQuaternion;
    }
    /**
     * Reset current rotation to baseline (creation/load-time orientation).
     */
    resetToBaselineRotation() {
        this.rotate(this._baselineQuaternion);
    }
    /**
     * Sets visibility
     * @param visible - Visibility state
     */
    setVisibility(visible) {
        if (!this._renderedObject)
            return;
        this._renderedObject.show = visible;
    }
    /**
     * Gets the model matrix (readonly)
     */
    get modelMatrix() {
        if (!this._renderedObject)
            return new Matrix4();
        return this._renderedObject.modelMatrix.clone();
    }
    /**
     * Gets anchor point in WGS84 based on anchorType
     * @returns WGS84 anchor position
     */
    getAnchorWgs84() {
        const dimensions = this._metaData.shapeConfig.dimensions;
        const anchorType = this._metaData.attributes.anchorType;
        const objectOriginAnchor = this.getObjectOriginAnchorOffset(dimensions, anchorType);
        if (!this._renderedObject) {
            return this._metaData.shapeConfig.wgs84Center.clone();
        }
        return Matrix4.multiplyByPoint(this._renderedObject.modelMatrix, objectOriginAnchor, new Cartesian3());
    }
    /**
     * Gets anchor point in local CRS coordinates.
     * @returns Local CRS anchor position
     */
    getAnchorLocal() {
        const sc = this._metaData.shapeConfig;
        const anchorOffset = this.getObjectOriginAnchorOffset(sc.dimensions, this._metaData.attributes.anchorType);
        return computeAnchorLocal(anchorOffset, sc.quaternion, sc.scale, sc.localCenter);
    }
    /**
     * 現在の基準点をローカルCRS座標で返す。通常は形状設定 (localCenter) から算出する。
     * localCenter が古いのはドラッグプレビュー中だけで、その間のみ modelMatrix 経由で変換する。
     * @returns ローカルCRS座標の基準点
     */
    async resolveAnchorLocal() {
        if (!this._localCenterStale)
            return this.getAnchorLocal();
        const [local] = await this._crsConverter.toLocal([this.getAnchorWgs84()]);
        return local;
    }
    getObjectOriginAnchorOffset(dimensions, anchorType) {
        return computeAnchorLocalOffset(anchorType, dimensions);
    }
    updateShapeConfig(params) {
        this._metaData.shapeConfig = {
            ...this._metaData.shapeConfig,
            ...params,
        };
    }
    /**
     * Re-resolves localCenter from wgs84Center through the CRS converter —
     * the ground-truth settle used at the create seam (builder), where a raw
     * ECEF translation makes the local value underivable. The frame is rebuilt
     * there with wgs84Center as its known ECEF origin, so the center stays put.
     * Mutators must NOT call this: translate(localCenter) and
     * translateToLocalAnchor() keep both centers in sync without a round trip.
     */
    async settleLocalCenterFromWgs84() {
        const wgs84Center = this._metaData.shapeConfig.wgs84Center.clone();
        const [localCenter] = await this._crsConverter.toLocal([wgs84Center]);
        this._localFrame = await resolveLocalFrame(this._crsConverter, localCenter, wgs84Center);
        this.updateShapeConfig({ localCenter });
        this.recomposeModelMatrix();
        this._localCenterStale = false;
    }
    /**
     * Updates attributes
     * @param params - Attributes to update
     */
    updateAttributes(params) {
        this._metaData.attributes = { ...this._metaData.attributes, ...params };
    }
    /**
     * Applies rotation (pivot around anchor point, kept fixed in local CRS)
     * @param quaternion - Rotation quaternion
     */
    rotate(quaternion) {
        if (!this._renderedObject)
            return;
        const shapeConfig = this._metaData.shapeConfig;
        const scale = shapeConfig.scale;
        // Anchor offset in object space (from center to anchor)
        const anchorOffset = this.getObjectOriginAnchorOffset(shapeConfig.dimensions, this._metaData.attributes.anchorType);
        // For local center calculation
        const anchorLocalBefore = computeAnchorLocal(anchorOffset, shapeConfig.quaternion, scale, shapeConfig.localCenter);
        // Anchor invariant keeps localCenter in sync without a CRS round trip
        const newLocalCenter = computeLocalCenterFromAnchor(anchorLocalBefore, anchorOffset, quaternion, scale);
        this.updateShapeConfig({
            quaternion,
            localCenter: newLocalCenter,
        });
        this.recomposeModelMatrix();
    }
    /**
     * Applies scale (pivot around anchor point, kept fixed in local CRS)
     * @param scale - Scale values
     */
    scale(scale) {
        if (!this._renderedObject)
            return;
        const shapeConfig = this._metaData.shapeConfig;
        const quaternion = shapeConfig.quaternion;
        // Anchor offset in object space (from center to anchor)
        const anchorOffset = this.getObjectOriginAnchorOffset(shapeConfig.dimensions, this._metaData.attributes.anchorType);
        // For local center calculation
        const anchorLocalBefore = computeAnchorLocal(anchorOffset, quaternion, shapeConfig.scale, shapeConfig.localCenter);
        // Anchor invariant keeps localCenter in sync without a CRS round trip
        const newLocalCenter = computeLocalCenterFromAnchor(anchorLocalBefore, anchorOffset, quaternion, scale);
        this.updateShapeConfig({
            scale,
            localCenter: newLocalCenter,
        });
        this.updateAppearanceForScale(scale);
        this.recomposeModelMatrix();
    }
    /**
     * Sets modelMatrix = compose(frame, localCenter, R, S) and derives
     * wgs84Center from it, so both always describe the same placement.
     */
    recomposeModelMatrix() {
        const sc = this._metaData.shapeConfig;
        const modelMatrix = composeModelMatrix(this._localFrame, sc.localCenter, sc.quaternion, sc.scale);
        if (this._renderedObject)
            this._renderedObject.modelMatrix = modelMatrix;
        this.updateShapeConfig({
            wgs84Center: Matrix4.getTranslation(modelMatrix, new Cartesian3()),
        });
    }
    /**
     * Moves the center to a nearby localCenter within the current frame
     * (small corrections such as the bottomZ constraint). Moves that may go
     * far use translateToLocalAnchor()/applyLocalTargetState(), which rebuild
     * the frame at the destination.
     * @param localCenter - The resulting localCenter, computed by the caller
     */
    translate(localCenter) {
        if (!this._renderedObject)
            return;
        this.updateShapeConfig({
            localCenter: Cartesian3.clone(localCenter),
        });
        this.recomposeModelMatrix();
        this._localCenterStale = false;
    }
    /**
     * ECEF-only rigid translation that leaves localCenter STALE (preview use
     * only, e.g. per-frame drag). The caller must settle afterwards via
     * translateToLocalAnchor() or settleLocalCenterFromWgs84().
     * @param translation - Translation vector in WGS84/ECEF
     */
    translatePreviewWgs84(translation) {
        if (!this._renderedObject)
            return;
        const modelMatrix = this._renderedObject.modelMatrix.clone();
        const newWgs84Center = Cartesian3.add(Matrix4.getTranslation(modelMatrix, new Cartesian3()), translation, new Cartesian3());
        this._renderedObject.modelMatrix = Matrix4.setTranslation(modelMatrix, newWgs84Center, modelMatrix);
        this.updateShapeConfig({
            wgs84Center: newWgs84Center,
        });
        this._localCenterStale = true;
    }
    /**
     * Moves the mesh so its anchor lands on the given local point. Async because the frame is rebuilt at the destination.
     * localCenter comes from the anchor invariant; the center is not round-tripped through the CRS.
     * @param target.targetLocalAnchor - Destination anchor in local coordinates
     */
    async translateToLocalAnchor(target) {
        const targetLocalAnchor = Cartesian3.clone(target.targetLocalAnchor);
        this._localFrame = await resolveLocalFrame(this._crsConverter, targetLocalAnchor);
        // Read q/s/anchorType after the await so a concurrent rotate/scale is kept
        const sc = this._metaData.shapeConfig;
        const anchorOffset = this.getObjectOriginAnchorOffset(sc.dimensions, this._metaData.attributes.anchorType);
        this.updateShapeConfig({
            localCenter: computeLocalCenterFromAnchor(targetLocalAnchor, anchorOffset, sc.quaternion, sc.scale),
        });
        this.recomposeModelMatrix();
        this._localCenterStale = false;
    }
    /**
     * True while localCenter is stale (after translatePreviewWgs84 and before
     * translateToLocalAnchor/settleLocalCenterFromWgs84 settles it).
     */
    get isLocalCenterStale() {
        return this._localCenterStale;
    }
    /**
     * Applies a fully-computed local-space target state in one shot.
     * Unlike sequential rotate()/scale()/translate(), no intermediate states
     * exist and no post-hoc anchor correction is needed: the caller computes
     * the final localCenter with all constraints already satisfied.
     * localCenter is authoritative (set verbatim, not derived by round-trip),
     * which keeps repeated applications of the same input bit-identical.
     */
    async applyLocalTargetState(target) {
        const localCenter = new Cartesian3(target.localCenter.x, target.localCenter.y, target.localCenter.z);
        this._localFrame = await resolveLocalFrame(this._crsConverter, localCenter);
        this.updateShapeConfig({
            quaternion: target.quaternion,
            scale: target.scale,
            localCenter,
        });
        this.recomposeModelMatrix();
        if (this._renderedObject)
            this.updateAppearanceForScale(target.scale);
        this._localCenterStale = false;
    }
    /**
     * Sets color
     * @param color - Color to set
     */
    setColor(color) {
        const currentAlpha = this._metaData.shapeConfig.color?.alpha ?? 1.0;
        const preservedColor = new Color(color.red, color.green, color.blue, currentAlpha);
        try {
            const appearance = this._renderedObject.appearance;
            if (appearance?.material?.uniforms) {
                appearance.material.uniforms.color = preservedColor;
            }
        }
        catch (e) {
            console.warn("Failed to update color:", e);
        }
        this.updateShapeConfig({ color: preservedColor });
    }
    /**
     * Sets transparency via color.alpha
     * @param alpha - Alpha value (0-1)
     */
    setTransparency(alpha) {
        const currentColor = this._metaData.shapeConfig.color;
        const newColor = new Color(currentColor.red, currentColor.green, currentColor.blue, alpha);
        try {
            const appearance = this._renderedObject.appearance;
            if (appearance?.material?.uniforms) {
                appearance.material.uniforms.color = newColor;
            }
        }
        catch (e) {
            console.warn("Failed to update transparency:", e);
        }
        this.updateShapeConfig({ color: newColor });
    }
    /**
     * Serializes to data object (Cesium types)
     * @returns Serialized data with Cesium types
     */
    toJson() {
        return {
            id: this._metaData.id,
            shapeConfig: this._metaData.shapeConfig,
            attributes: this._metaData.attributes,
        };
    }
    updateAppearanceForScale(scale) {
        const isMirrored = scale.x * scale.y * scale.z < 0;
        if (this._isMirrored === isMirrored)
            return;
        this._isMirrored = isMirrored;
        const appearance = this._renderedObject.appearance;
        if (!(appearance instanceof MaterialAppearance))
            return;
        const renderState = {
            ...appearance.renderState,
            frontFace: isMirrored
                ? WindingOrder.CLOCKWISE
                : WindingOrder.COUNTER_CLOCKWISE,
        };
        this._renderedObject.appearance = new MaterialAppearance({
            material: appearance.material,
            translucent: appearance.translucent,
            closed: appearance.closed,
            flat: appearance.flat,
            faceForward: appearance.faceForward,
            materialSupport: appearance.materialSupport,
            vertexShaderSource: appearance.vertexShaderSource,
            fragmentShaderSource: appearance.fragmentShaderSource,
            renderState,
        });
    }
    /**
     * Computes ENU-aligned AABB in WGS84 coordinates
     * Returns AABB that is aligned to local ENU axes (East-North-Up)
     */
    solidMeshAABB_WGS84() {
        const shapeConfig = this._metaData.shapeConfig;
        // Get OBB vertices with rotation applied (in local ENU space)
        const { vertices } = getOBBVerticesFromShapeConfig(shapeConfig);
        // Compute local AABB from rotated vertices
        const { aabb: localAABB } = computeLocalAABBFromVertices(vertices);
        // Transform to WGS84 using ENU frame
        const enuMatrix = Transforms.eastNorthUpToFixedFrame(shapeConfig.wgs84Center);
        const min = Matrix4.multiplyByPoint(enuMatrix, localAABB.minimum, new Cartesian3());
        const max = Matrix4.multiplyByPoint(enuMatrix, localAABB.maximum, new Cartesian3());
        const center = Matrix4.multiplyByPoint(enuMatrix, localAABB.center, new Cartesian3());
        return new AxisAlignedBoundingBox(min, max, center);
    }
    /**
     * Disposes resources
     */
    dispose() {
        if (!this._renderedObject)
            return;
        try {
            const removed = this._removePrimitive();
            if (removed && !this._renderedObject.isDestroyed()) {
                this._renderedObject.destroy();
            }
        }
        catch (error) {
            console.error("Error disposing solid mesh:", error);
        }
        this._renderedObject = null;
    }
}
//# sourceMappingURL=solid-mesh.js.map